/**
 * The minimal foundation needed by rm3 to run in the browser.
 * Must be loaded before anything else.
 * Uses a window global to share object references with '@rm3/framework/browser/Shared' which
 * is a module included in all bundles.
 *
 * Relies on a global object on the window to load the framework properly:
 * _rm3_bootstrapConfig
 */

/* istanbul ignore file */ // framework loading code is implicitly tested enough that we do not need explicit tests

window._rm3_shared = window._rm3_shared || {};
window._rm3_shared.window = window._rm3_shared.window || window;

// All the libraries that are available on window._rm_shared must be imported before this line
import { LicenseInfo } from '@mui/x-license';
import { shared } from '@rm3/framework/browser/Shared';
import { forConfig as loaderForConfig } from '@rm3/exports/browser/loader';

if (!window._rm3_bootstrapConfig) {
	throw new Error("'window._rm3_bootstrapConfig' must be set!");
}

function reportError(message, err) {
	// SHOULDDO: Handle error better with feedback in UI and reporting to logging endpoint. - https://roadmunk.atlassian.net/browse/RMK-8463
	console.error(`[rm3] ${message}`, err);
	if (window._rm3_bootstrapConfig.envMode === 'local-test') {
		throw err;
	}
}

/**
 * Starts the framework by:
 * 1) Loading the config from @tempo-io/roadmapping-common-config (which pulls from window.env set in `main.html.ejs`)
 * 2) Loading the lib and rmf bundle
 * 3) Initializes the rm3 framework using these bundles
 *
 * After `injectBootloader` has resolved all promises, the rmf shell can be used
 * @param bootstrapConfig
 */
function injectBootloader(bootstrapConfig) {
	// Ensure minimum bootstrap config is provided
	if (!bootstrapConfig.envMode) {
		throw new Error("'bootstrapConfig.envMode' must be set!");
	}
	if (!bootstrapConfig.loader) {
		throw new Error("'bootstrapConfig.loader' must be set!");
	}

	// Set start marker
	shared._bootStartTime = Date.now();

	// Init loader
	shared.loader = shared.loader || loaderForConfig(bootstrapConfig);

	// Initiate config load
	if (shared.config) {
		shared.configPromise = Promise.resolve(shared.config);
	}
	if (shared.configPromise) {
		shared._configLoadedTime = Date.now();
	} else {
		shared.configPromise = (async function () {
			// this import is a dynamic import so that the window.env is set prior to execution
			const { clientConfig } = await import('@tempo-io/roadmapping-common-config');

			shared.config = clientConfig;

			LicenseInfo.setLicenseKey(clientConfig.roadmapping.muiProLicenseKey);

			shared._configLoadedTime = Date.now();
			return shared.config;
		})().catch((err) => {
			reportError('Error calling config endpoint!', err);
		});
	}

	// Initiate vendor load
	if (shared.vendorsPromise) {
		shared._vendorsLoadedTime = Date.now();
		Promise.resolve(shared.vendorsPromise);
	} else {
		shared.vendorsPromise = (async function () {
			await shared.loader.loadSharedBundle('vendor');
			shared._vendorsLoadedTime = Date.now();
			return shared._vendorsLoadedTime;
		})().catch((err) => {
			reportError('Error loading lib!', err);
		});
	}

	// Initiate lib load
	if (shared.lib) {
		shared.libPromise = Promise.resolve(shared.lib);
	}
	if (shared.libPromise) {
		shared._libLoadedTime = Date.now();
	} else {
		shared.libPromise = (async function () {
			await shared.vendorsPromise;
			shared.lib = await shared.loader.loadSharedBundle('lib');
			shared._libLoadedTime = Date.now();
			return shared.lib;
		})().catch((err) => {
			reportError('Error loading lib!', err);
		});
	}

	// Initiate framework load
	if (shared.rmfFactory) {
		shared.rmfFactoryPromise = Promise.resolve(shared.rmfFactory);
	}
	if (shared.rmfFactoryPromise) {
		shared._rmfLoadedTime = Date.now();
	} else {
		shared.rmfFactoryPromise = (async function () {
			await shared.vendorsPromise;
			shared.rmfFactory = await shared.loader.loadSharedBundle('rmf');

			shared._rmfLoadedTime = Date.now();

			return shared.rmfFactory;
		})().catch((err) => {
			reportError('Error loading framework!', err);
		});
	}
	if (shared.rmf) {
		shared.rmfPromise = Promise.resolve(shared.rmf);
	}
	if (shared.rmfPromise) {
		shared._rmfInitializedTime = Date.now();
	} else {
		shared.rmfPromise = (async function () {
			await shared.vendorsPromise;
			const rmfFactory = await shared.rmfFactoryPromise;

			const initExports = rmfFactory.initForContext({
				lib: await shared.libPromise,
			});

			const config = await shared.configPromise;

			await initExports.init(config);

			// SHOULDDO: Hook up destroy that should run before the page unloads. Maybe using `navigator.sendBeacon`. - https://roadmunk.atlassian.net/browse/RMK-7356
			// await initExports.destroy();

			shared.rmf = initExports.rmf as rmfT.IRMClientFramework;
			shared.lib = initExports.rmf.lib as rmfT.ClientLibT;

			if (config.envMode === 'local-dev') {
				// Expose as global to simplify running commands in browser console.
				// Component code must NOT make use of this as a global.
				window.rmf = shared.rmf;
			}

			shared._rmfInitializedTime = Date.now();

			const { boolVariation } = await import('@rm3/lib/feature-flags/FeatureFlagsUserClient');

			shared.lib.Moment.initClient(window, config.loader.momentLocaleUrlTemplate);
			// this defaults to en locale if exp_rm_isENMomentLocaleEnforced_boolean = true, else the users browsers locale is set
			if (!boolVariation('exp_rm_isENMomentLocaleEnforced_boolean')) {
				await shared.lib.Moment.setLocale(...(window.navigator.languages || [window.navigator.language]));
			}
			return shared.rmf;
		})().catch((err) => {
			reportError('Error initializing framework!', err);
		});
	}
}

// ########################################
// # Bootstrap the rm3 UI Shell
// ########################################

injectBootloader(window._rm3_bootstrapConfig);

if (shared.shell) {
	shared.shellPromise = Promise.resolve(shared.shell);
}
if (shared.shellPromise) {
	shared._shellLoadedTime = Date.now();
} else {
	shared.shellPromise = (async function () {
		await shared.vendorsPromise;
		await shared.rmfPromise;

		shared.defaultQueryClient = new window._rm3_shared['@tanstack/react-query'].QueryClient({
			defaultOptions: {
				queries: {
					staleTime: 5 * (60 * 1000), // 5 mins
					gcTime: 10 * (60 * 1000), // 10 mins
				},
			},
		});
		shared.shell = await shared.loader.loadSharedBundle('shell');
		shared._shellLoadedTime = Date.now();
		return shared.shell;
	})().catch((err) => {
		reportError('Error loading UI Shell!', err);
	});
}

// Start the shell
shared.shellPromise
	.then(async () => {
		// SHOULDDO: Pass in page context by awaiting it and set on 'Shell'. - https://roadmunk.atlassian.net/browse/RMK-7407

		if (!window._rm3_pageLoadTime) {
			throw new Error("'window._rm3_pageLoadTime' is not set!");
		}

		shared._msToShellLoaded = shared._shellLoadedTime - window._rm3_pageLoadTime;
		shared.pageConfigs = await (
			await import('@tempo-io/roadmapping-frontend-sidebar')
		).generatePageConfig(shared.config.userContext, shared.shell.getHttpClient());

		if (window._rm3_bootstrapConfig.envMode === 'local-dev') {
			console.log('[rm3] UI Shell booted:', {
				msToShellLoaded: shared._msToShellLoaded,
			});
		}

		if (window._rm3_bootstrapConfig.shell?.pages?.urlPathPrefix) {
			const prefix = window._rm3_bootstrapConfig.shell.pages.urlPathPrefix;
			if (window.location.pathname.substring(0, prefix.length) === prefix) {
				const el = document.getElementById(window._rm3_bootstrapConfig.shell.pages.injectAtElementId);
				if (!el) {
					throw new Error(`Element with ID '${window._rm3_bootstrapConfig.shell.pages.injectAtElementId}' not found!`);
				}
				if (!window._rm3_bootstrapConfig.shell.pages.rootComponentId) {
					throw new Error(`Cannot boot SPA due to '_rm3_bootstrapConfig.shell.pages.rootComponentId' property not set!`);
				}
				const rootComponentId = window._rm3_bootstrapConfig.shell.pages.rootComponentId;
				// NOTE: We cannot just derive the URI of the root component from the URL
				//       because the URL may be a pushState URL created by a component with sub-routes.
				//       The server knows all the routes and will set 'rootComponentId' to the part-most component
				//       which is the component that can boot the root of the app and setup the sub-routes again.
				//       i.e. This will not work: 'const rootComponentId = window.location.pathname.substring(prefix.length);'
				shared.shell.renderIntoFrameworkInternalOnly({ el, componentId: rootComponentId, componentType: 'page' });
			}
		}
	})
	.catch((err) => {
		reportError('Error booting UI Shell!', err);
	});
